import React, { useEffect, useRef, useState } from 'react';
import { DiscoverProvider, useDiscoverContext } from '../contexts/DiscoverContext';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { PATHS } from './paths';
import { ProtectedRoute, PublicOnlyRoute } from '../components/ProtectedRoute';
import { useUserData } from '../components/UserDataContext';
import { useAuthGate } from '../contexts/AuthGateContext';
import { EVENTS, SCREENS } from '../constants/navigation';
import { PageTransition } from '../components/ui/PageTransition';

// ── Lazy screen imports ────────────────────────────────────────────────────────
import { ScreenLoader } from '../components/ScreenLoader';
import { SuspendedScreen } from '../components/SuspendedScreen';
import {
  ScreenTransitionProvider,
  useScreenReady,
  useTransitionLoaderVisible,
} from '../contexts/ScreenTransitionContext';
import { OnboardingScreen } from '../components/OnboardingScreen';
import { LoginScreen } from '../components/LoginScreen';
import { SignupScreen } from '../components/SignupScreen';
import { ProfileSetupScreen } from '../components/ProfileSetupScreen';
import { DiscoverScreen } from '../components/DiscoverScreen';
import { DiscoverScreenV2 } from '../components/DiscoverScreenV2';
import { YapTabScreen } from '../components/YapTabScreen';
import { ExploreScreen } from '../components/ExploreScreen';
import { ChatScreen } from '../components/ChatScreen';
import { ProfileScreen } from '../components/ProfileScreen';
import { EditProfileScreen } from '../components/EditProfileScreen';
import { NotificationsScreen } from '../components/NotificationsScreen';
import { AppSettingsScreen } from '../components/AppSettingsScreen';
import { SafetySettingsScreen } from '../components/SafetySettingsScreen';
import { BlockedUsersScreen } from '../components/BlockedUsersScreen';
import { MutedWordsScreen } from '../components/MutedWordsScreen';
import { YapDetailScreen } from '../components/YapDetailScreen';
import { ShoutDetailsScreen } from '../components/ShoutDetailsScreen';
import { YapShoutsScreen } from '../components/YapShoutsScreen';
import { RoomScreen } from '../components/RoomScreen';
import { TribeGroupScreen } from '../components/TribeGroupScreen';
import { HistoryForumScreen } from '../components/HistoryForumScreen';
import { DirectMessageScreen } from '../components/DirectMessageScreen';
import { EventDetailsScreen } from '../components/EventDetailsScreen';
import { AllTribesScreen } from '../components/AllTribesScreen';
import { AllForumsScreen } from '../components/AllForumsScreen';
import { AllHistoryScreen } from '../components/AllHistoryScreen';
import { AllCountriesScreen } from '../components/AllCountriesScreen';
import { AllFlagsScreenWrapper } from '../components/AllFlagsScreenWrapper';
import { CountrySelectionScreen } from '../components/CountrySelectionScreen';
import { UserYapsScreen } from '../components/UserYapsScreen';
import { FriendYapsScreen } from '../components/FriendYapsScreen';
import { ReportScreen } from '../components/ReportScreen';
import { LegalScreen } from '../components/LegalScreen';
import { WatcherDashboard } from '../components/WatcherDashboard';
import { BottomNav } from '../components/BottomNav';
import { PublicProfilePage } from '../components/PublicProfilePage';
import { MyBusinessScreen } from '../components/business/MyBusinessScreen';
import { BusinessProfileBuilderScreen } from '../components/business/BusinessProfileBuilderScreen';
import { BusinessProfileScreen } from '../components/business/BusinessProfileScreen';
import { PurchaseCheckPrompt } from '../components/business/PurchaseCheckPrompt';
import { usePurchaseReturn } from '../hooks/usePurchaseReturn';
import { MatchPopup } from '../components/MatchPopup';
import { InviteFriendsModal } from '../components/InviteFriendsModal';
import { GovernanceModalManager } from '../components/GovernanceModalManager';
import { ModeratorDashboard } from '../components/ModeratorDashboard';
import { GrowthDashboard } from '../components/GrowthDashboard';
import { AdCreativeStudio } from '../components/AdCreativeStudio';
import { DailyRewardManager } from '../components/DailyRewardManager';
import { SecuritySetupModal } from '../components/SecuritySetupModal';
import { PopupQueueProvider, usePopupSlot, POPUP_PRIORITY } from '../contexts/PopupQueueContext';
import { LegionRemote } from '../components/LegionRemote';
import { WatcherRemote } from '../components/WatcherRemote';
import { WatcherGuidelinesModal } from '../components/WatcherGuidelinesModal';
import { hasEverAccepted, stampAccepted } from '../utils/guidelinesGate';
import { YapDayTimer } from '../components/YapDayTimer';
import { SupportLinks } from '../components/SupportLinks';
import { YapTabGuidelinesModal } from '../components/YapTabGuidelinesModal';
import { GuideProvider, useGuide } from '../guide/useGuide';
import { GuideOverlay } from '../guide/GuideOverlay';
import { SafeStorage } from '../utils/safeStorage';
import { TribeInvitePage } from '../components/TribeInvitePage';
import { ForumInvitePage } from '../components/ForumInvitePage';
import { RoomInvitePage } from '../components/RoomInvitePage';
import { useDeepLinks } from '../hooks/useDeepLinks';
import { consumePendingGroupInvite } from '../utils/inviteCode';

// ── Remote kill-switch: featureFlags/discover.v2ForAll ───────────────────────
//
// Firestore document `featureFlags/discover` controls the rollout at runtime
// with NO redeploy required — works identically on web and on installed iOS
// builds because Capacitor runs the same JS bundle and uses the same Firestore
// SDK.
//
// Kill-switch operation (instant, no build):
//   • To DISABLE v2 for everyone: set featureFlags/discover.v2ForAll = false
//   • To RE-ENABLE v2 for everyone: set featureFlags/discover.v2ForAll = true
//     (or delete the field — missing/absent defaults to V2 on)
//
// Fail-open rationale: if the Firestore read fails, we default to V2 (true)
// rather than V1. Reasoning: the whole app requires Firestore — a read failure
// here means the entire session is already broken. Defaulting to V2 keeps the
// user experience consistent rather than silently downgrading them. If you ever
// want fail-safe-to-V1 behaviour instead, flip the catch return to `false`.
//
// Module-level singleton so the read fires ONCE per app session, not on every
// re-render of DiscoverScreenSelector.
let _v2FlagCache: boolean | null = null;
let _v2FlagPromise: Promise<boolean> | null = null;

async function getV2Flag(): Promise<boolean> {
  if (_v2FlagCache !== null) return _v2FlagCache;
  if (!_v2FlagPromise) {
    _v2FlagPromise = (async () => {
      try {
        const { getDbSafe } = await import('../services/firebaseConfig');
        const { doc, getDoc } = await import('firebase/firestore');
        const db = await getDbSafe();
        const snap = await getDoc(doc(db, 'featureFlags', 'discover'));
        // Missing doc OR missing field → default V2 on (true)
        const value = snap.exists() ? snap.data().v2ForAll !== false : true;
        _v2FlagCache = value;
        return value;
      } catch {
        // Read failure → fail-open to V2
        _v2FlagCache = true;
        return true;
      }
    })();
  }
  return _v2FlagPromise;
}

// ── V1/V2 feature flag selector ───────────────────────────────────────────────
// Default: V2 for all users (kill-switch at featureFlags/discover.v2ForAll).
// Per-user override: betaDiscover: true on the user doc forces V2 even when
// the kill-switch is off (useful for testing after a kill-switch rollback).
function DiscoverScreenSelector({ onMatch }: { onMatch: (d: any) => void }) {
  const { userProfile } = useUserData();
  // null = flag not yet loaded; true/false = resolved value
  const [v2Flag, setV2Flag] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    getV2Flag().then(flag => { if (!cancelled) setV2Flag(flag); });
    return () => { cancelled = true; };
  }, []);

  // Reporting keeps the transition overlay up until the flag resolves, so
  // neither V1 nor V2 paints before we know which one to show. The loader is
  // rendered here too rather than returning null: on a cold load — a refresh or
  // the app opening on Discover — no transition is in flight to cover this, and
  // returning null showed a black screen for the length of the Firestore read.
  useScreenReady(v2Flag !== null);
  if (v2Flag === null) return <ScreenLoader />;

  if (v2Flag || userProfile?.betaDiscover === true) {
    return <DiscoverScreenV2 onMatch={onMatch} />;
  }
  return <DiscoverScreen onMatch={onMatch} />;
}

// ── Screen wrappers that bridge URL params → component props ──────────────────

function YapDetailRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { yapId } = useParams();
  const yap = location.state?.yap || (yapId ? { id: yapId } : null);
  if (!yap) return <Navigate to={PATHS.YAP_TAB} replace />;
  return <YapDetailScreen yap={yap} onBack={() => navigate(-1)} onYapUpdate={() => window.dispatchEvent(new CustomEvent('yapUpdated'))} />;
}

function ShoutDetailRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const { shoutId } = useParams();
  const { removeShout, updateShoutCaption } = useUserData();
  // Fall back to an id-only stub exactly like YapDetailRoute does. Without this a
  // shout URL only worked when navigated to in-app with router state attached:
  // refreshing /shout/:id, opening a shared link, or following a push
  // notification bounced the user to the Yap tab. ShoutDetailsScreen re-fetches
  // the full document on mount, so the stub is enough to render from.
  const shout = location.state?.shout || (shoutId ? { id: shoutId } : null);
  if (!shout) return <Navigate to={PATHS.YAP_TAB} replace />;
  return (
    <ShoutDetailsScreen
      shout={shout}
      onBack={() => navigate(-1)}
      onDeleteShout={() => navigate(-1)}
      onDeleteCaption={() => updateShoutCaption(shout.originalYap?.id || shout.id, undefined, undefined)}
    />
  );
}

function RoomRoute() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  // Id-only, like YapShoutsRoute: RoomScreen subscribes to the room itself, so a
  // refresh or a notification tap needs no router state to land correctly.
  if (!roomId) return <Navigate to={PATHS.CHAT} replace />;
  return <RoomScreen roomId={String(roomId)} onBack={() => navigate(PATHS.CHAT)} />;
}

function YapShoutsRoute() {
  const navigate = useNavigate();
  const { yapId } = useParams();
  // Nothing but the id is needed, so this route survives a refresh or a shared
  // URL without any router state — unlike FriendYapsRoute, which bounces.
  if (!yapId) return <Navigate to={PATHS.YAP_TAB} replace />;
  return <YapShoutsScreen yapId={String(yapId)} onBack={() => navigate(-1)} />;
}

function FriendProfileRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const userId = params.userId;
  const user = location.state?.user || (userId ? { id: userId } : null);
  const { userProfile } = useUserData();
  if (userId && String(userId) === String(userProfile?.id)) {
    return <Navigate to={PATHS.PROFILE} replace />;
  }
  if (!user) return <Navigate to={PATHS.DISCOVER} replace />;
  return <ProfileScreen key={`profile-friend-${userId}`} userId={userId} user={user} onBack={() => navigate(-1)} />;
}

function FriendYapsRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const friend = location.state?.friend;
  if (!friend) return <Navigate to={PATHS.PROFILE} replace />;
  return <FriendYapsScreen friend={friend} onBack={() => navigate(-1)} />;
}

function TribeGroupRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get('invite') || undefined;
  const tribe = location.state?.tribe || (params.tribeId ? { id: params.tribeId } : null);
  if (!tribe) return <Navigate to={PATHS.EXPLORE} replace />;
  // If the tribe was opened via a deep link / invite (it's the first entry in
  // history, e.g. the invite page redirected here with replace:true), there's
  // nothing to pop back to — drop the user into Explore so the app behaves as
  // if they'd been browsing all along. Otherwise pop the stack normally.
  const handleBack = () => {
    const idx = (window.history.state && (window.history.state as any).idx) ?? 0;
    if (idx <= 0) navigate(PATHS.EXPLORE);
    else navigate(-1);
  };
  return <TribeGroupScreen tribe={tribe} onBack={handleBack} initialInviteCode={invite} />;
}

function HistoryForumRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const topic = location.state?.topic || (params.forumId ? { id: params.forumId } : null);
  if (!topic) return <Navigate to={PATHS.EXPLORE} replace />;
  // Same reasoning as TribeGroupRoute: a forum opened from an invite link is the
  // first entry in history, so there's nothing to pop back to.
  const handleBack = () => {
    const idx = (window.history.state && (window.history.state as any).idx) ?? 0;
    if (idx <= 0) navigate(PATHS.EXPLORE);
    else navigate(-1);
  };
  return <HistoryForumScreen topic={topic} onBack={handleBack} />;
}

function DirectMessageRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const stateFriend = location.state?.friend;
  const isRequest = !!location.state?.isRequest;
  const [friend, setFriend] = useState<any>(stateFriend ?? null);
  const [loading, setLoading] = useState(!stateFriend && !!params.userId);

  // Cold-launch fallback: when the user opens the screen from a push notification
  // there is no route state; fetch the friend by URL param instead of bouncing out.
  useEffect(() => {
    if (stateFriend || !params.userId) return;
    let cancelled = false;
    (async () => {
      try {
        const { UserService } = await import('../services/api');
        const u = await UserService.fetchUserById(String(params.userId));
        if (!cancelled) {
          setFriend(u || null);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params.userId, stateFriend]);

  useScreenReady(!loading);
  if (loading) return null;
  if (!friend) return <Navigate to={PATHS.CHAT} replace />;
  return <DirectMessageScreen friend={friend} isRequest={isRequest} onBack={() => navigate(-1)} />;
}

function EventDetailsRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const event = location.state?.event;
  return <EventDetailsScreen event={event} onBack={() => navigate(-1)} />;
}

function LegalRoute() {
  const navigate = useNavigate();
  const params = useParams();
  const type = params.type;
  if (!type) return <Navigate to={PATHS.EXPLORE} replace />;
  return <LegalScreen type={type as any} onBack={() => navigate(-1)} />;
}

function ReportRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const { targetId, targetType, context } = location.state || {};
  return <ReportScreen onBack={() => navigate(-1)} targetId={targetId} targetType={targetType} context={context} />;
}

// ── Bottom nav tabs that show the nav bar ─────────────────────────────────────
const BOTTOM_NAV_PATHS = [PATHS.DISCOVER, PATHS.YAP_TAB, PATHS.EXPLORE, PATHS.CHAT, PATHS.PROFILE, PATHS.ALL_COUNTRIES];

// Prompt copy shown when a logged-out visitor taps an auth-gated bottom-nav tab.
const GATED_TAB_LABELS: Record<string, string> = {
  [SCREENS.DISCOVER]: 'Create an account to meet people',
  [SCREENS.EXPLORE]:  'Create an account to explore tribes & forums',
  [SCREENS.CHAT]:     'Create an account to start chatting',
  [SCREENS.PROFILE]:  'Create an account to build your profile',
};

function pathToScreen(pathname: string): string {
  if (pathname.startsWith('/discover')) return SCREENS.DISCOVER;
  if (pathname.startsWith('/yaptab'))   return SCREENS.YAP_TAB;
  if (pathname.startsWith('/explore'))  return SCREENS.EXPLORE;
  if (pathname.startsWith('/countries')) return SCREENS.EXPLORE;
  if (pathname.startsWith('/chat'))     return SCREENS.CHAT;
  if (pathname === '/profile')          return SCREENS.PROFILE;
  return '';
}

// ── Main router ───────────────────────────────────────────────────────────────
/**
 * The community guidelines appear once, for a new account, and never again on
 * their own. After that they live behind the Rules button in Settings.
 *
 * Acceptance is recorded in two places, and either one suppresses the modal.
 * The flag on the user document is the real answer, because "once per account"
 * cannot be answered by a device: a reinstall or a second browser would
 * otherwise re-prompt someone who has already agreed. The localStorage stamp is
 * the fast path — the profile arrives a beat after auth, and without it the
 * modal would flash on every launch in that gap.
 */
const GUIDELINES_KEY = 'guidelinesAcceptedAt_v1:';

function guidelinesAlreadyAccepted(uid: string, profile: any): boolean {
  return !!profile?.guidelinesAcceptedAt || hasEverAccepted(GUIDELINES_KEY, uid);
}

/** Records acceptance on the account, and locally so the next launch is instant. */
async function recordGuidelinesAccepted(uid: string) {
  stampAccepted(GUIDELINES_KEY, uid);
  try {
    const { getDbSafe } = await import('../services/firebaseConfig');
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const db = await getDbSafe();
    await updateDoc(doc(db, 'users', uid), { guidelinesAcceptedAt: serverTimestamp() });
  } catch (err) {
    // The local stamp already did the job on this device; the write is what
    // carries it to the next one. Not worth interrupting the user for.
    console.warn('[guidelines] could not persist acceptance to the profile:', err);
  }
}

function AppRouterInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, firebaseUser, authLoading, profileSettled, banState, removeShout, updateShoutCaption } = useUserData();
  const { isAuthed, openAuthPrompt } = useAuthGate();
  const pushRouteHandledRef = useRef(false);
  const bootStartedAtRef = useRef(Date.now());
  const [showSplash, setShowSplash] = useState(true);
  const [matchData, setMatchData] = useState<any>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showModDashboard, setShowModDashboard] = useState(false);
  const [showGrowthDashboard, setShowGrowthDashboard] = useState(false);
  const [showAdStudio, setShowAdStudio] = useState(false);
  const [showNewUserSecurityPrompt, setShowNewUserSecurityPrompt] = useState(false);
  const [showGlobalGuidelines, setShowGlobalGuidelines] = useState(false);

  // Startup popups take turns instead of stacking.
  //
  // Each still owns its own "should I show at all" state; these only answer
  // "is it my turn". A popup whose turn it is not keeps its intent and waits,
  // so nothing is lost — the PIN prompt no longer swallows the guidelines
  // behind it, and whichever is next appears as soon as the front one closes.
  const securitySlot = usePopupSlot(
    'security-setup', POPUP_PRIORITY.SECURITY, showNewUserSecurityPrompt,
  );
  const guidelinesSlot = usePopupSlot(
    'guidelines', POPUP_PRIORITY.GUIDELINES, showGlobalGuidelines,
  );
  // Always "wants": DailyRewardManager runs its own eligibility checks, so this
  // is purely about not landing on top of something more important.
  const dailyRewardSlot = usePopupSlot(
    'daily-reward', POPUP_PRIORITY.DAILY_REWARD, true,
  );
  const [yapTabInitialTab, setYapTabInitialTab] = useState<'world' | 'friends'>('world');
  // The remotes are HIDDEN for the duration rather than floated above the dim.
  // Kept visible they would sit over the darkened area on every step, competing
  // for attention with the thing being pointed at, and the tour never points at
  // them. Restored the moment it ends.
  const { active: guideActive } = useGuide();

  // iOS Universal Link routing (Phase 8)
  useDeepLinks();

  // ── Pending push-route replay ────────────────────────────────────────────────
  // A notification tap (App.tsx) navigates immediately AND persists its target.
  // On a cold start that immediate navigate can be dropped while auth/profile are
  // still settling (ProtectedRoute renders null during that window), leaving the
  // user on a blank/default screen. Once the app is genuinely ready we replay the
  // persisted target so the tap always lands on what the user clicked. Runs once
  // per app session (ref guard); the timestamp guard ignores stale routes.
  useEffect(() => {
    if (pushRouteHandledRef.current) return;
    if (authLoading || !profileSettled) return; // not ready yet
    pushRouteHandledRef.current = true;
    try {
      const raw = sessionStorage.getItem('pendingPushRoute_v1');
      if (!raw) return;
      sessionStorage.removeItem('pendingPushRoute_v1');
      const { path, ts } = JSON.parse(raw);
      if (!path || (Date.now() - ts) > 20000) return; // ignore stale taps
      const current = location.pathname + location.search;
      if (current !== path) navigate(path, { replace: true });
    } catch (_) { /* sessionStorage/JSON failure — nothing to replay */ }
  }, [authLoading, profileSettled, navigate, location.pathname, location.search]);

  // The boot splash tracks the auth check rather than a fixed 3s wait: a warm
  // start no longer sits on the logo after the app is ready, and a slow one is
  // no longer cut off mid-check. The floor keeps it from flashing; the ceiling
  // keeps a stalled auth check from trapping the user (ScreenLoader offers a
  // reload after 15s if the app somehow never gets past this).
  useEffect(() => {
    if (authLoading) return;
    const elapsed = Date.now() - bootStartedAtRef.current;
    const t = setTimeout(() => setShowSplash(false), Math.max(0, 900 - elapsed));
    return () => clearTimeout(t);
  }, [authLoading]);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 8000);
    return () => clearTimeout(t);
  }, []);

  // ── Legacy CustomEvent bridge ────────────────────────────────────────────────
  useEffect(() => {
    const nav = (screen: string, detail?: any) => {
      switch (screen) {
        case SCREENS.DISCOVER:      return navigate(PATHS.DISCOVER);
        case SCREENS.YAP_TAB:       return navigate(PATHS.YAP_TAB);
        case SCREENS.EXPLORE:       return navigate(PATHS.EXPLORE);
        case SCREENS.CHAT:          return navigate(PATHS.CHAT);
        case SCREENS.PROFILE:       return navigate(PATHS.PROFILE);
        case SCREENS.NOTIFICATIONS: return navigate(PATHS.NOTIFICATIONS);
        case SCREENS.SETTINGS:      return navigate(PATHS.SETTINGS);
        case SCREENS.SAFETY:        return navigate(PATHS.SAFETY);
        case SCREENS.EDIT_PROFILE:  return navigate(PATHS.PROFILE_EDIT);
        case SCREENS.ALL_TRIBES:    return navigate(PATHS.ALL_TRIBES);
        case SCREENS.ALL_FORUMS:    return navigate(PATHS.ALL_FORUMS);
        case SCREENS.ALL_HISTORY:   return navigate(PATHS.ALL_HISTORY);
        case SCREENS.ALL_COUNTRIES: return navigate(PATHS.ALL_COUNTRIES);
        case SCREENS.ALL_FLAGS:     return navigate(PATHS.PROFILE_FLAGS);
        case SCREENS.BLOCKED_USERS: return navigate(PATHS.BLOCKED_USERS);
        case SCREENS.MUTED_WORDS:   return navigate(PATHS.MUTED_WORDS);
        case SCREENS.REPORT:        return navigate(PATHS.REPORT);
        case SCREENS.LOGIN:         return navigate(PATHS.LOGIN, { replace: true, state: { from: null } });
        case SCREENS.SIGNUP:        return navigate(PATHS.SIGNUP);
        case SCREENS.PROFILE_SETUP: return navigate(PATHS.PROFILE_SETUP);
        case SCREENS.USER_YAPS:     return navigate(PATHS.PROFILE_YAPS);
        case 'legal':               return navigate(`/legal/${detail?.type || 'terms'}`);
        default:                    return;
      }
    };

    const handleNavigate = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (typeof d === 'string') nav(d);
      else if (d?.screen) nav(d.screen, d);
    };

    const handleYapDetail = (e: Event) => {
      const yap = (e as CustomEvent).detail;
      if (yap?.id) navigate(`/yap/${yap.id}`, { state: { yap } });
    };

    const handleShoutDetail = (e: Event) => {
      const shout = (e as CustomEvent).detail;
      if (shout?.id) navigate(`/shout/${shout.id}`, { state: { shout } });
    };

    const handleRoom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const id = typeof detail === 'string' ? detail : detail?.id;
      if (id) navigate('/room/' + id);
    };

    const handleYapShouts = (e: Event) => {
      const yapId = (e as CustomEvent).detail;
      if (yapId) navigate(`/yap/${yapId}/shouts`);
    };

    const handleFriendProfile = (e: Event) => {
      const friend = (e as CustomEvent).detail;
      if (!friend?.id) return;
      if (userProfile && String(friend.id) === String(userProfile.id)) {
        navigate(PATHS.PROFILE);
      } else {
        navigate(`/profile/${friend.id}`, { state: { user: friend } });
      }
    };

    const handleTribeGroup = (e: Event) => {
      const tribe = (e as CustomEvent).detail;
      if (tribe?.id) navigate(`/tribe/${tribe.id}`, { state: { tribe } });
    };

    const handleHistoryForum = (e: Event) => {
      const topic = (e as CustomEvent).detail;
      if (topic?.id) navigate(`/forum/${topic.id}`, { state: { topic } });
    };

    const handleDirectMessage = (e: Event) => {
      const friend = (e as CustomEvent).detail;
      if (friend?.id) navigate(`/messages/${friend.id}`, { state: { friend } });
    };

    const handleMatch     = (e: Event) => setMatchData((e as CustomEvent).detail);
    const handleInvite    = ()          => setShowInviteModal(true);
    const handleModDash      = ()       => {
      if (userProfile?.rank === 'watcher' || userProfile?.rank === 'legion') {
        setShowModDashboard(true);
      }
    };
    const handleGrowthDash   = ()       => setShowGrowthDashboard(true);
    const handleAdStudio     = ()       => setShowAdStudio(true);
    const handleTabChange = (e: Event)  => setYapTabInitialTab((e as CustomEvent).detail);
    const handleFriendYapsTab = ()      => { setYapTabInitialTab('friends'); navigate(PATHS.YAP_TAB); };
    const handleFriendYaps = (e: Event) => {
      const friend = (e as CustomEvent).detail;
      if (friend?.id) navigate(`/profile/${friend.id}/yaps`, { state: { friend } });
    };
    const handleEventDetails = (e: Event) => {
      const ev = (e as CustomEvent).detail;
      if (ev?.id) navigate(`/event/${ev.id}`, { state: { event: ev } });
    };

    // Business profiles route the same way Rooms do: a CustomEvent bridge, so a
    // notification tap and an in-app tap land on the same navigate call.
    const handleBusinessProfile = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const id = typeof detail === 'string' ? detail : detail?.id;
      if (id) navigate(`/business/${id}`);
    };
    const handleMyBusiness = () => navigate(PATHS.MY_BUSINESS);

    window.addEventListener(EVENTS.NAVIGATE_TO_SCREEN,       handleNavigate);
    window.addEventListener(EVENTS.NAVIGATE_TO_YAP_DETAIL,   handleYapDetail);
    window.addEventListener(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, handleShoutDetail);
    window.addEventListener(EVENTS.NAVIGATE_TO_YAP_SHOUTS,   handleYapShouts);
    window.addEventListener(EVENTS.NAVIGATE_TO_ROOM,         handleRoom);
    window.addEventListener(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, handleFriendProfile);
    window.addEventListener(EVENTS.NAVIGATE_TO_TRIBE_GROUP,  handleTribeGroup);
    window.addEventListener(EVENTS.NAVIGATE_TO_HISTORY_FORUM, handleHistoryForum);
    window.addEventListener('navigateToDirectMessage',        handleDirectMessage);
    window.addEventListener(EVENTS.SHOW_MATCH_POPUP,         handleMatch);
    window.addEventListener('profileMatch',                   handleMatch);
    window.addEventListener(EVENTS.SHOW_INVITE_MODAL as any, handleInvite);
    window.addEventListener('openModeratorDashboard',  handleModDash);
    window.addEventListener('openGrowthDashboard',     handleGrowthDash);
    window.addEventListener('openAdCreativeStudio',    handleAdStudio);
    window.addEventListener('yapTabChange',                   handleTabChange);
    window.addEventListener('navigateToFriendYapsTab',        handleFriendYapsTab);
    window.addEventListener(EVENTS.NAVIGATE_TO_FRIEND_YAPS,   handleFriendYaps);
    window.addEventListener(EVENTS.NAVIGATE_TO_EVENT_DETAILS, handleEventDetails);
    window.addEventListener(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, handleBusinessProfile);
    window.addEventListener(EVENTS.NAVIGATE_TO_MY_BUSINESS,      handleMyBusiness);
    window.addEventListener('navigateToScreen',               handleNavigate);
    window.addEventListener('navigateToHistoryForum',         handleHistoryForum);
    window.addEventListener('navigateToTribeGroup',           handleTribeGroup);
    window.addEventListener('navigateToFriendProfile',        handleFriendProfile);

    return () => {
      window.removeEventListener(EVENTS.NAVIGATE_TO_SCREEN,       handleNavigate);
      window.removeEventListener(EVENTS.NAVIGATE_TO_YAP_DETAIL,   handleYapDetail);
      window.removeEventListener(EVENTS.NAVIGATE_TO_SHOUT_DETAIL, handleShoutDetail);
      window.removeEventListener(EVENTS.NAVIGATE_TO_YAP_SHOUTS,   handleYapShouts);
      window.removeEventListener(EVENTS.NAVIGATE_TO_ROOM,         handleRoom);
      window.removeEventListener(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, handleFriendProfile);
      window.removeEventListener(EVENTS.NAVIGATE_TO_TRIBE_GROUP,  handleTribeGroup);
      window.removeEventListener(EVENTS.NAVIGATE_TO_HISTORY_FORUM, handleHistoryForum);
      window.removeEventListener('navigateToDirectMessage',        handleDirectMessage);
      window.removeEventListener(EVENTS.SHOW_MATCH_POPUP,         handleMatch);
      window.removeEventListener('profileMatch',                   handleMatch);
      window.removeEventListener(EVENTS.SHOW_INVITE_MODAL as any, handleInvite);
      window.removeEventListener('openModeratorDashboard',  handleModDash);
      window.removeEventListener('openGrowthDashboard',     handleGrowthDash);
      window.removeEventListener('openAdCreativeStudio',    handleAdStudio);
      window.removeEventListener('yapTabChange',                   handleTabChange);
      window.removeEventListener('navigateToFriendYapsTab',        handleFriendYapsTab);
      window.removeEventListener(EVENTS.NAVIGATE_TO_FRIEND_YAPS,   handleFriendYaps);
      window.removeEventListener(EVENTS.NAVIGATE_TO_EVENT_DETAILS, handleEventDetails);
      window.removeEventListener(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, handleBusinessProfile);
      window.removeEventListener(EVENTS.NAVIGATE_TO_MY_BUSINESS,      handleMyBusiness);
      window.removeEventListener('navigateToScreen',               handleNavigate);
      window.removeEventListener('navigateToHistoryForum',         handleHistoryForum);
      window.removeEventListener('navigateToTribeGroup',           handleTribeGroup);
      window.removeEventListener('navigateToFriendProfile',        handleFriendProfile);
    };
  }, [navigate, userProfile]);

  // Guidelines gate
  useEffect(() => {
    const preAuth = [PATHS.ONBOARDING, PATHS.LOGIN, PATHS.SIGNUP, PATHS.PROFILE_SETUP];
    const isPreAuth = preAuth.some(p => location.pathname.startsWith(p));
    if (isPreAuth) return;
    // Logged-out visitors browsing the public YapTab preview don't get the rules
    // popup — the first thing an anonymous visitor sees should be the feed, not a
    // consent wall. They agree to the guidelines during signup instead, and the
    // modal shows on their first authenticated session as usual.
    if (authLoading) return;
    if (!firebaseUser) return;
    // Wait for the profile before deciding. The acceptance flag lives on it, and
    // the profile lands a beat after auth — deciding early would re-prompt
    // someone who accepted on another device, which is the entire reason the
    // flag is on the account rather than only in localStorage.
    if (!profileSettled) return;
    // Only an account that has just finished onboarding gets the modal. Existing
    // accounts never see it and reach the rules through the Rules button in
    // Settings, so this is not a gate on "have you accepted" — it is a gate on
    // "did you just sign up". ProfileSetupScreen sets the flag on completion and
    // accepting clears it.
    if (SafeStorage.getItem('pending_guidelines') !== 'true') return;
    if (guidelinesAlreadyAccepted(firebaseUser.uid, userProfile)) return;
    // Skip the guidelines modal if this session was opened via a push
    // notification tap — the user is jumping straight to specific content
    // they were notified about, so reshowing the rules would be noise.
    // App.tsx sets this flag from both the native Capacitor listener and
    // the web service-worker PUSH_NAV handler.
    if (sessionStorage.getItem('enteredFromPush_v1') === 'true') return;
    // Re-check the flag when the timer fires (not just now): on a cold launch
    // the native push-tap callback can set the flag a beat after this effect
    // first runs, so a check-only-upfront approach still flashed the modal.
    const t = setTimeout(() => {
      if (SafeStorage.getItem('pending_guidelines') !== 'true') return;
      if (guidelinesAlreadyAccepted(firebaseUser.uid, userProfile)) return;
      if (sessionStorage.getItem('enteredFromPush_v1') === 'true') return;
      setShowGlobalGuidelines(true);
    }, 1000);
    return () => clearTimeout(t);
  }, [location.pathname, authLoading, firebaseUser, profileSettled, userProfile]);

  // Hard suppression: if a push tap is detected at any point, dismiss the
  // guidelines modal even if it already opened before the flag was set.
  useEffect(() => {
    const onEnteredFromPush = () => setShowGlobalGuidelines(false);
    window.addEventListener('enteredFromPush', onEnteredFromPush);
    return () => window.removeEventListener('enteredFromPush', onEnteredFromPush);
  }, []);

  // Store leave/return detection, mounted once at the app shell.
  //
  // Only ever acts on a resume that follows a real Store-link tap — the arming
  // flag set in BusinessProfileScreen — so an ordinary task-switch never asks
  // anybody whether they completed a purchase. See usePurchaseReturn.
  const { pendingCheck, respond: respondToPurchaseCheck, dismiss: dismissPurchaseCheck } =
    usePurchaseReturn(userProfile?.id ? String(userProfile.id) : null);

  const transitionLoaderVisible = useTransitionLoaderVisible();
  const { swipeModeActive } = useDiscoverContext();
  const showBottomNav = BOTTOM_NAV_PATHS.some(p => location.pathname === p) && !showSplash && !swipeModeActive;
  const currentScreenForNav = pathToScreen(location.pathname);

  const isPreAuth = [PATHS.ONBOARDING, PATHS.LOGIN, PATHS.SIGNUP, PATHS.PROFILE_SETUP]
    .some(p => location.pathname.startsWith(p) || location.pathname === '/');

  // 🚫 SUSPENSION GATE. Returns before the app shell, so a suspended account gets
  // the explanation INSTEAD of the app rather than on top of it — no routes, no
  // bottom nav, no modals, nothing to tap past.
  //
  // banState comes from the live profile snapshot in UserDataContext, so a ban
  // issued mid-session lands here on the next render rather than at next launch,
  // and a lifted ban restores the app the same way without a relaunch.
  //
  // Placed after every hook above: React requires a consistent hook count per
  // render, and an early return above them would change it the moment someone is
  // banned — the exact crash pattern that took the app down earlier this session.
  if (banState.banned) {
    return <SuspendedScreen />;
  }

  return (
    <div
      className="app-container w-full max-w-[600px] mx-auto h-screen bg-[#eddeb7] dark:bg-[#0D0D0D] overflow-hidden relative"
      style={{ height: '100dvh' }}
    >
      <YapTabGuidelinesModal
        isOpen={guidelinesSlot}
        // Stamped on accept rather than on show: someone who force-quits without
        // accepting has not agreed to anything, and should be asked again.
        onAccept={() => {
          SafeStorage.removeItem('pending_guidelines');
          if (firebaseUser) void recordGuidelinesAccepted(firebaseUser.uid);
          setShowGlobalGuidelines(false);
        }}
      />

      {!isPreAuth && !guideActive && (
        <>
          <LegionRemote />
          <WatcherRemote />
          <WatcherGuidelinesModal />
        </>
      )}

      <GuideOverlay />

      {/* Global SVG Gradients */}
      <svg width="0" height="0" className="absolute invisible pointer-events-none" aria-hidden="true">
        <defs>
          <linearGradient id="brand-gradient-svg" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F39C12" />
            <stop offset="100%" stopColor="#E74C3C" />
          </linearGradient>
          <linearGradient id="brand-gradient-blue-svg" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4478ea" />
            <stop offset="100%" stopColor="#2d1ef1" />
          </linearGradient>
        </defs>
      </svg>

      <ScreenLoader variant="boot" visible={showSplash} />

      {/* The transition overlay. Sits above the route content and below the
          boot splash, and holds until the destination screen reports ready.
          Stays mounted and fades on the flag: see the note on ScreenLoader's
          `visible` prop for why AnimatePresence cannot own this one. */}
      <ScreenLoader visible={transitionLoaderVisible && !showSplash} />

      <div className={showSplash ? 'opacity-0' : 'opacity-100 transition-opacity duration-500 relative h-full'}>
        {/* Leave this nesting alone. The key sits on <Routes> and on
            PageTransition's inner motion.div, both grandchildren of
            AnimatePresence, so AnimatePresence never sees a child swap and the
            exit animations never run. That looks like dead code and is not:
            `initial={false}` propagates down through PresenceContext to every
            motion descendant in the app, which is what makes screen content
            mount at its final state instead of animating up from `initial`.

            Moving the key onto the direct child, so exits became real, broke the
            app twice. With mode="wait" the destination waited on an outgoing
            exit that never completes, and the app froze on whichever screen
            loaded first. Dropping AnimatePresence entirely then took
            `initial={false}` with it, and every motion.div with an
            `initial={{ opacity: 0 }}` stayed at opacity 0: replies, list items
            and whole sections rendered into the DOM, correctly sized, invisible.

            The screen-to-screen transition the user sees is the ScreenLoader
            overlay above, which does not depend on any of this. */}
        <AnimatePresence mode="wait" initial={false}>
          <PageTransition>
            <Routes location={location} key={location.pathname}>
              {/* Root redirect — everyone lands on the YapTab feed. Logged-out
                  visitors get the public preview (10-yap wall); logged-in users
                  get the full feed. */}
              <Route path="/" element={<Navigate to={PATHS.YAP_TAB} replace />} />

              {/* Public tribe invite landing page — no auth required */}
              <Route path="/tribes/:tribeId" element={<TribeInvitePage />} />

              {/* Public forum invite landing page — the same deal for forums */}
              <Route path="/forums/:forumId" element={<ForumInvitePage />} />

              {/* Public room invite landing page. Plural /rooms to match
                  /tribes and /forums, and distinct from the in-app /room/:id
                  which is members-only. */}
              <Route path="/rooms/:roomId" element={<RoomInvitePage />} />

              {/* Public profile preview — no auth required; all actions gated behind signup CTA */}
              <Route path="/p/:userId" element={<PublicProfilePage />} />

              {/* Public business profile — no auth required. The Firestore rule
                  scopes the unauthenticated read to active profiles, so a
                  suspended one 404s here exactly as it disappears from Explore
                  and search. Signed-in owners get the editing controls on the
                  same page. */}
              <Route path="/business/:profileId" element={<BusinessProfileScreen />} />

              {/* Pre-auth routes */}
              <Route path={PATHS.ONBOARDING} element={<PublicOnlyRoute><OnboardingScreen onComplete={() => navigate(PATHS.LOGIN)} /></PublicOnlyRoute>} />
              <Route path={PATHS.LOGIN}      element={<PublicOnlyRoute><LoginScreen onLogin={() => {}} onSignup={() => navigate(PATHS.SIGNUP)} onSignupComplete={() => navigate(PATHS.PROFILE_SETUP)} /></PublicOnlyRoute>} />
              <Route path={PATHS.SIGNUP}     element={<PublicOnlyRoute><SignupScreen onSignupComplete={() => navigate(PATHS.PROFILE_SETUP)} onBack={() => navigate(PATHS.LOGIN)} /></PublicOnlyRoute>} />
              <Route path={PATHS.PROFILE_SETUP} element={
                <PublicOnlyRoute>
                  <ProfileSetupScreen initialName="" onComplete={() => {
                    SafeStorage.removeItem('profileSetupProgress');
                    // A signup that started from a tribe/forum invite ends on that
                    // group, not Explore — otherwise the new member never sees the
                    // thing they clicked.
                    const pendingPath = consumePendingGroupInvite();
                    navigate(pendingPath || PATHS.EXPLORE, pendingPath ? { replace: true } : undefined);
                    setTimeout(() => setShowNewUserSecurityPrompt(true), 1200);
                  }} />
                </PublicOnlyRoute>
              } />

              {/* Main app routes — all protected */}
              <Route path={PATHS.DISCOVER}   element={<ProtectedRoute><DiscoverScreenSelector onMatch={(d) => setMatchData(d)} /></ProtectedRoute>} />
              <Route path={PATHS.DISCOVER_COUNTRY} element={<ProtectedRoute><DiscoverScreen onMatch={(d) => setMatchData(d)} /></ProtectedRoute>} />
              {/* Public: logged-out visitors browse the YapTab world feed (10-yap
                  preview wall); interactive actions prompt account creation. The
                  Friends tab remains auth-gated (see YAP_TAB_FRIENDS below). */}
              <Route path={PATHS.YAP_TAB}    element={<YapTabScreen initialTab={yapTabInitialTab} onTabChange={setYapTabInitialTab} />} />
              <Route path={PATHS.YAP_TAB_FRIENDS} element={<ProtectedRoute><YapTabScreen initialTab="friends" onTabChange={setYapTabInitialTab} /></ProtectedRoute>} />
              <Route path={PATHS.EXPLORE}    element={<ProtectedRoute><ExploreScreen /></ProtectedRoute>} />
              <Route path={PATHS.CHAT}       element={<ProtectedRoute><ChatScreen /></ProtectedRoute>} />

              {/* Profile routes */}
              <Route path={PATHS.PROFILE}      element={<ProtectedRoute><ProfileScreen key="profile-self" onSettings={() => navigate(PATHS.SETTINGS)} onSafety={() => navigate(PATHS.SAFETY)} /></ProtectedRoute>} />
              <Route path={PATHS.PROFILE_EDIT} element={<ProtectedRoute><EditProfileScreen onBack={() => navigate(-1)} onAllFlags={() => navigate(PATHS.PROFILE_FLAGS)} /></ProtectedRoute>} />
              <Route path={PATHS.PROFILE_YAPS} element={<ProtectedRoute><UserYapsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.PROFILE_FLAGS} element={<ProtectedRoute><AllFlagsScreenWrapper onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path="/profile/:userId/yaps" element={<ProtectedRoute><FriendYapsRoute /></ProtectedRoute>} />
              <Route path="/profile/:userId"  element={<ProtectedRoute><FriendProfileRoute /></ProtectedRoute>} />

              {/* App screens */}
              <Route path={PATHS.NOTIFICATIONS} element={<ProtectedRoute><NotificationsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.SETTINGS}      element={<ProtectedRoute><AppSettingsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.SAFETY}        element={<ProtectedRoute><SafetySettingsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.BLOCKED_USERS} element={<ProtectedRoute><BlockedUsersScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.MUTED_WORDS} element={<ProtectedRoute><MutedWordsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />

              {/* Content detail */}
              <Route path="/yap/:yapId"        element={<ProtectedRoute><YapDetailRoute /></ProtectedRoute>} />
              <Route path="/shout/:shoutId"    element={<ProtectedRoute><ShoutDetailRoute /></ProtectedRoute>} />
              <Route path="/yap/:yapId/shouts" element={<ProtectedRoute><YapShoutsRoute /></ProtectedRoute>} />
              <Route path="/room/:roomId"     element={<ProtectedRoute><RoomRoute /></ProtectedRoute>} />
              <Route path="/tribe/:tribeId"    element={<ProtectedRoute><TribeGroupRoute /></ProtectedRoute>} />
              <Route path="/forum/:forumId"    element={<ProtectedRoute><HistoryForumRoute /></ProtectedRoute>} />
              <Route path="/messages/:userId"  element={<ProtectedRoute><DirectMessageRoute /></ProtectedRoute>} />
              <Route path="/event/:eventId"    element={<ProtectedRoute><EventDetailsRoute /></ProtectedRoute>} />

              {/* Explore sub-screens */}
              <Route path={PATHS.ALL_TRIBES}        element={<ProtectedRoute><AllTribesScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.ALL_FORUMS}        element={<ProtectedRoute><AllForumsScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.ALL_HISTORY}       element={<ProtectedRoute><AllHistoryScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.ALL_COUNTRIES}     element={<ProtectedRoute><AllCountriesScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path={PATHS.COUNTRY_SELECTION} element={<ProtectedRoute><CountrySelectionScreen currentCountry="" onSave={() => navigate(-1)} onBack={() => navigate(-1)} /></ProtectedRoute>} />

              {/* Misc */}
              <Route path={PATHS.REPORT}            element={<ProtectedRoute><ReportRoute /></ProtectedRoute>} />
              {/* Public on purpose. Two reasons:
                  1. App Review and Google Play both require a privacy policy
                     URL reachable WITHOUT signing in, which an auth-gated route
                     can never be.
                  2. ProtectedRoute redirects an incomplete profile to /setup,
                     so during onboarding this route bounced straight back and
                     the Terms link appeared to do nothing. */}
              <Route path="/legal/:type"            element={<LegalRoute />} />
              <Route path={PATHS.WATCHER_DASHBOARD} element={<ProtectedRoute><WatcherDashboard onBack={() => navigate(-1)} /></ProtectedRoute>} />

              {/* Business owner surfaces — auth-gated, unlike /business/:id above */}
              <Route path={PATHS.MY_BUSINESS} element={<ProtectedRoute><MyBusinessScreen onBack={() => navigate(-1)} /></ProtectedRoute>} />
              <Route path="/my-business/build/:registrationId" element={<ProtectedRoute><BusinessProfileBuilderScreen onBack={() => navigate(PATHS.MY_BUSINESS)} /></ProtectedRoute>} />

              {/* Catch-all */}
              <Route path="*" element={<Navigate to={PATHS.EXPLORE} replace />} />
            </Routes>
          </PageTransition>
        </AnimatePresence>
      </div>

      {showBottomNav && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[600px] z-50"
        >
          <BottomNav
            currentScreen={currentScreenForNav}
            onNavigate={(screen) => {
              // Logged-out visitor: every tab except YapTab is auth-gated. Open the
              // signup prompt over the feed instead of hard-redirecting to /login —
              // being ejected from the page you're reading is the fastest way to
              // lose a visitor who hasn't decided to join yet.
              if (!isAuthed && screen !== SCREENS.YAP_TAB) {
                openAuthPrompt(GATED_TAB_LABELS[screen] || 'Create a free account to continue');
                return;
              }
              window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_SCREEN, { detail: screen }));
            }}
            onNotifications={() => {
              if (!isAuthed) { openAuthPrompt('Create an account to get notifications'); return; }
              navigate(PATHS.NOTIFICATIONS);
            }}
          />
        </motion.div>
      )}

      {/* The floating buttons are portalled to <body>, outside the route
          content the transition overlay sits on top of, and the overlay is
          translucent — so on every navigation they showed through it, pinned
          over a screen that had not arrived yet. Gating them on the same flag
          holds them back until the destination is actually on screen. */}
      <YapDayTimer
        visible={location.pathname.startsWith('/yaptab') && !showSplash && !transitionLoaderVisible}
      />
      {/* Community/support links live on Explore now, not the feed. */}
      <SupportLinks
        visible={location.pathname.startsWith('/explore') && !showSplash && !transitionLoaderVisible}
      />

      {matchData && (
        <MatchPopup
          user={matchData}
          userProfile={userProfile}
          onClose={() => setMatchData(null)}
          onViewProfile={() => {
            const d = matchData;
            setMatchData(null);
            navigate(`/profile/${d.id}`, { state: { user: d } });
          }}
        />
      )}

      <PurchaseCheckPrompt
        check={pendingCheck}
        onRespond={(confirmed) => void respondToPurchaseCheck(confirmed)}
        onDismiss={dismissPurchaseCheck}
      />

      <InviteFriendsModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} onRewarded={() => setShowInviteModal(false)} />
      <GovernanceModalManager />
      <ModeratorDashboard isOpen={showModDashboard} onClose={() => setShowModDashboard(false)} />
      <GrowthDashboard isOpen={showGrowthDashboard} onClose={() => setShowGrowthDashboard(false)} />
      <AdCreativeStudio isOpen={showAdStudio} onClose={() => setShowAdStudio(false)} />
      {/* blockShow was the only sequencing that existed: one hardcoded pair,
          one direction. The queue now decides, so this asks it. */}
      <DailyRewardManager currentScreen={currentScreenForNav} blockShow={!dailyRewardSlot} />

      {securitySlot && (
        <SecuritySetupModal showSkip={true} onClose={() => {
          setShowNewUserSecurityPrompt(false);
          SafeStorage.removeItem('pending_security_setup');
        }} />
      )}
    </div>
  );
}

export default function AppRouter() {
  return (
    <ScreenTransitionProvider>
      <DiscoverProvider>
        {/* Above AppRouterInner: every startup popup lives inside it and has to
            share one queue, or they stack the way they used to. */}
        <PopupQueueProvider>
          {/* Inside PopupQueueProvider so the guide can take a turn in the
              queue, and inside the Router so it can navigate between steps. */}
          <GuideProvider>
            <AppRouterInner />
          </GuideProvider>
        </PopupQueueProvider>
      </DiscoverProvider>
    </ScreenTransitionProvider>
  );
}
